'use client';

import React, { useState, useRef, useEffect } from 'react';
import styles from './DatePicker.module.css';

export interface DatePickerProps {
  label?: string;
  value?: string;
  onChange: (date: string) => void;
  placeholder?: string;
  error?: string | boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  min?: string;
  max?: string;
  stubErrorSpace?: boolean;
}

const DatePicker: React.FC<DatePickerProps> = ({
  label,
  value = '',
  onChange,
  placeholder,
  error,
  disabled = false,
  fullWidth = false,
  min,
  max,
  stubErrorSpace,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(
    value ? new Date(value) : null
  );
  const [currentMonth, setCurrentMonth] = useState<Date>(
    value ? new Date(value) : new Date()
  );
  const containerRef = useRef<HTMLDivElement>(null);

  // Синхронизация внешнего value с внутренним состоянием
  useEffect(() => {
    if (value) {
      const newDate = new Date(value);
      setSelectedDate(newDate);
      setCurrentMonth(newDate);
    } else {
      setSelectedDate(null);
    }
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const formatDate = (date: Date | null): string => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDisplayDate = (date: Date | null): string => {
    if (!date) return '';
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    };
    return date.toLocaleDateString('en-US', options);
  };

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    onChange(formatDate(date));
    setIsOpen(false);
  };

  const getDaysInMonth = (date: Date): Date[] => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days: Date[] = [];

    // Add empty cells for days before the start of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      const prevMonthDay = new Date(year, month, -startingDayOfWeek + i + 1);
      days.push(prevMonthDay);
    }

    // Add days of the current month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }

    return days;
  };

  const isDateDisabled = (date: Date): boolean => {
    const dateStr = formatDate(date);
    if (min && dateStr < min) return true;
    if (max && dateStr > max) return true;
    return false;
  };

  const isToday = (date: Date): boolean => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const isSameDay = (date1: Date | null, date2: Date): boolean => {
    if (!date1) return false;
    return (
      date1.getDate() === date2.getDate() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear()
    );
  };

  const isCurrentMonth = (date: Date): boolean => {
    return date.getMonth() === currentMonth.getMonth();
  };

  const previousMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1)
    );
  };

  const nextMonth = () => {
      setCurrentMonth(
          new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1)
      );
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const weekDays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  return (
      <div
          ref={containerRef}
          className={`${styles.container} ${fullWidth ? styles.container__fullWidth : ''}`}
    >
          {label && <label className={styles.label}>{label}</label>}

          <div className={styles.inputWrapper}>
              <input
                  type="text"
                  className={`${styles.input} ${error ? styles.input__error : ''}`}
                  value={formatDisplayDate(selectedDate)}
                  placeholder={placeholder}
                  readOnly
                  onClick={() => !disabled && setIsOpen(!isOpen)}
                  disabled={disabled}
            />
              <div className={styles.iconWrapper} onClick={() => !disabled && setIsOpen(!isOpen)}>
                  <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                >
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                      <line x1="16" y1="2" x2="16" y2="6"></line>
                      <line x1="8" y1="2" x2="8" y2="6"></line>
                      <line x1="3" y1="10" x2="21" y2="10"></line>
                  </svg>
              </div>
          </div>

          {isOpen && (
          <div className={styles.calendar}>
              <div className={styles.calendarHeader}>
                  <button
                      type="button"
                      className={styles.navButton}
                      onClick={previousMonth}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="15 18 9 12 15 6"></polyline>
                      </svg>
                  </button>
                  <div className={styles.monthYear}>
                      {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                  </div>
                  <button
                      type="button"
                      className={styles.navButton}
                      onClick={nextMonth}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="9 18 15 12 9 6"></polyline>
                      </svg>
                  </button>
              </div>

              <div className={styles.weekDays}>
                  {weekDays.map(day => (
                      <div key={day} className={styles.weekDay}>
                          {day}
                      </div>
                    ))}
              </div>

              <div className={styles.daysGrid}>
                  {getDaysInMonth(currentMonth).map((date, index) => {
                        const isDisabled = isDateDisabled(date);
                        const isSelected = isSameDay(selectedDate, date);
                        const isTodayDate = isToday(date);
                        const isOtherMonth = !isCurrentMonth(date);

                        return (
                            <button
                                key={index}
                                type="button"
                                className={`${styles.day} ${isSelected ? styles.day__selected : ''} ${
                                    isTodayDate ? styles.day__today : ''
                                } ${isOtherMonth ? styles.day__otherMonth : ''}`}
                                onClick={() => !isDisabled && handleDateSelect(date)}
                                disabled={isDisabled}
                            >
                                {date.getDate()}
                            </button>
                        );
                    })}
              </div>
          </div>
        )}

          {(typeof error === 'string' || stubErrorSpace) && (
          <div className={`${styles.message} ${error ? styles.message__error : ''}`}>
              {typeof error === 'string' ? error : ''}
          </div>
        )}
      </div>
  );
};

export default DatePicker;
